import type { Package, PackageManageable } from "../types/package";
import type { APIResponse } from "../types/common";

import { processError } from "../utils";

export type CreatePackageOptions = {
  name: string;
  slug: string;
  description: string;
  type: string;
  contentRating: string;
  previewKeys?: string[];
  thumbnailKeys?: string[];
  tags?: string[];
  version: {
    name: string;
    clientSize: number;
    serverSize: number;
    config: Record<string, unknown>;
    dependencyIds: string[];
  };
};
export async function createPackage(
  bearerToken: string,
  options: CreatePackageOptions
): Promise<APIResponse<PackageManageable>> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/packages`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${bearerToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(options),
      }
    );

    if (response.status === 200) {
      const data = await response.json();
      return data;
    }

    let error = "Unknown error";
    switch (response.status) {
      case 400:
        error = "Invalid input";
        break;
      case 401:
        error = "Logged out";
        break;
      case 409:
        error = "Package slug taken";
        break;
    }

    return {
      success: false,
      error,
    };
  } catch (e) {
    return {
      success: false,
      error: processError(e),
    };
  }
}

export async function getPublicPackages(
  bearerToken: string,
  {
    slug = "",
    page = 1,
    limit = 16,
  }: {
    slug?: string;
    page?: number;
    limit?: number;
  }
): Promise<APIResponse<Package[]>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (typeof slug === "string" && slug !== "") params.append("slug", slug);

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/packages?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching packages:", error);
    return { success: false, error: processError(error) };
  }
}

export async function getCreatedPackages(
  bearerToken: string,
  {
    slug = "",
    page = 1,
    limit = 16,
  }: {
    slug?: string;
    page?: number;
    limit?: number;
  }
): Promise<APIResponse<PackageManageable[]>> {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (typeof slug === "string" && slug !== "") params.append("slug", slug);

    const response = await fetch(
      `${
        process.env.REACT_APP_API_BASE
      }/api/v1/packages/me?${params.toString()}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching user packages:", error);
    return { success: false, error: processError(error) };
  }
}

export async function getPackageById(
  bearerToken: string,
  packageId: string
): Promise<APIResponse<Package>> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/packages/${packageId}`,
      {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      }
    );

    if (response.status !== 200)
      return {
        success: false,
        error: "Logged out",
      };

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error("Error fetching package:", error);
    return { success: false, error: processError(error) };
  }
}

type UpdatePackageOptions = Partial<{
  name: string;
  slug: string;
  description: string;
  type: string;
  contentRating: string;
  previewKeys: string[];
  thumbnailKeys: string[];
  tags: string[];
}>;
export async function updatePackage(
  bearerToken: string,
  packageId: string,
  options: UpdatePackageOptions
): Promise<APIResponse<PackageManageable>> {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_API_BASE}/api/v1/packages/${packageId}`,
      {
        method: "PUT",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${bearerToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(options),
      }
    );
    if (response.status === 200) {
      const data = await response.json();
      return { success: true, data };
    }
    let error = "Unknown error";
    switch (response.status) {
      case 400:
        error = "Invalid input";
        break;
      case 401:
        error = "Logged out";
        break;
      case 403:
        error = "Permission denied";
        break;
      case 404:
        error = "Package not found";
        break;
    }
    return { success: false, error };
  } catch (error) {
    console.error("Error updating package:", error);
    return { success: false, error: processError(error) };
  }
}
